import { Box, Card, CardContent, Tooltip, Typography } from "@mui/material";
import { WarningAmberRounded } from "@mui/icons-material";
import type { ReactNode } from "react";
import type { KpiEmphasis } from "../metrics.types";

interface KpiTileProps {
  label: string;
  value: ReactNode;
  /** "why this number / how it's computed" — shown on hover + focus. */
  tooltip: string;
  emphasis?: KpiEmphasis;
}

const VALUE_COLOR: Record<KpiEmphasis, string> = {
  critical: "error.main",
  watch: "info.main",
  none: "text.primary",
};

/**
 * One tile in the Metrics Dashboard KPI wall: a small uppercase label, a big
 * value, and a hover/focus tooltip carrying the definition.
 *
 * `critical` is signalled by the red value **and** a warning icon in the label
 * (so it isn't colour-alone — WCAG 1.4.1); no tinted background or border.
 * `error.main` on the card surface clears AA for the 20px bold value in both
 * themes. `watch` just colours the value.
 */
export function KpiTile({ label, value, tooltip, emphasis = "none" }: KpiTileProps) {
  return (
    <Tooltip title={tooltip}>
      <Card
        variant="outlined"
        tabIndex={0}
        aria-label={`${label}: ${String(value)}`}
        sx={{ height: "100%" }}
      >
        <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 0.5,
              minHeight: "2.2em",
            }}
          >
            {emphasis === "critical" && (
              <WarningAmberRounded
                aria-hidden
                sx={{ fontSize: 13, mt: "1px", color: "error.main", flexShrink: 0 }}
              />
            )}
            <Typography
              component="div"
              sx={{
                fontSize: 10.5,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                color: "text.secondary",
                lineHeight: 1.3,
              }}
            >
              {label}
            </Typography>
          </Box>
          <Typography
            component="div"
            sx={{ mt: 0.5, fontSize: 20, fontWeight: 700, color: VALUE_COLOR[emphasis] }}
          >
            {value}
          </Typography>
        </CardContent>
      </Card>
    </Tooltip>
  );
}
